<template>
  <div
    v-if="videoUrl || course.cover_url || teacher?.cover_url"
    class="group relative aspect-video overflow-hidden rounded-lg"
  >
    <div class="bg-primary-950 absolute -bottom-2 h-2 w-full blur-2xl" />

    <div v-if="videoUrl" class="absolute inset-0">
      <app-video
        autoplay
        :preview="preview"
        :src="videoUrl"
        @timeupdate="onVideoTimeUpdate"
      />

      <app-fade-transition>
        <div
          v-show="preview"
          class="absolute inset-0 grid cursor-pointer place-content-center transition group-hover:bg-black/30"
          tabindex="0"
          @click="onPlayClick"
        >
          <app-button
            class="scale-95 opacity-0 transition group-hover:scale-100 group-hover:opacity-100"
            color="light"
            icon="ph:play-circle"
            size="lg"
            tabindex="-1"
          >
            {{ $t("course.page.watch_full_video_label") }}
          </app-button>
        </div>
      </app-fade-transition>
    </div>

    <app-image
      v-else-if="course.cover_url"
      alt=""
      class="size-full object-cover"
      :height="1080"
      :src="course.cover_url"
      :width="1920"
    />

    <teacher-card-profile
      v-else-if="teacher?.cover_url"
      class="size-full"
      cover
      :teacher="teacher"
    />
  </div>
</template>

<script lang="ts" setup>
const properties = defineProps<{
  teacher?: Pick<
    DatabaseTable<"teachers">,
    "id" | "full_name" | "job" | "avatar_url" | "pinned" | "cover_url"
  > & {
    companies: DatabaseTable<"companies">[];
  };
  course: Pick<DatabaseTable<"courses">, "cover_url" | "video_url">;
}>();

const { $analytics } = useNuxtApp();

const preview = ref(true);

const onPlayClick = () => {
  preview.value = false;
  $analytics.track("course_video_played");
};

const videoProgressInPercent = ref(0);
const TRACKED_PROGRESS_STEPS = [25, 50, 75] as const;

TRACKED_PROGRESS_STEPS.forEach((step) => {
  whenever(
    () => videoProgressInPercent.value > step,
    () => {
      $analytics.track("course_video_watched", { progress_in_percent: step });
    },
  );
});

const onVideoTimeUpdate = ({
  currentTime,
  totalTime,
}: {
  currentTime: number;
  totalTime: number;
}) => {
  videoProgressInPercent.value = Math.floor((currentTime * 100) / totalTime);
};

const videoUrl = useCloudinaryVideo(properties.course.video_url);
</script>
